/**
 * ترجمات المنتجات (اسم + وصف) بالعربيّة والإنجليزيّة.
 *
 * مصدر الحقيقة عند العرض: قاعدتنا (`product_translations`) → metafields Shopify
 * (`descriptions.ar/en`, `title_ar/en`) → نصّ Shopify الأصليّ.
 * سبب وجود الطبقة المحليّة: تطبيق Shopify الحاليّ لا يملك صلاحيّة الكتابة على
 * المنتجات/metafields، فنتحكّم بالترجمة من عندنا الآن، وننقلها لـShopify لاحقاً
 * بدون تغيير أيّ كود عرض.
 */
import { createServerFn } from "@tanstack/react-start";
import { lazyValidator } from "@/lib/server/lazy-validator";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";

export type ProductTranslationRow = {
  handle: string;
  title_ar: string | null;
  title_en: string | null;
  description_ar: string | null;
  description_en: string | null;
  source: string;
  updated_at: string;
  notes_top_ar?: string | null;
  notes_top_en?: string | null;
  notes_middle_ar?: string | null;
  notes_middle_en?: string | null;
  notes_base_ar?: string | null;
  notes_base_en?: string | null;
};

export type ProductTranslationMap = Record<
  string,
  {
    titleAr: string;
    titleEn: string;
    descriptionAr: string;
    descriptionEn: string;
    notesTopAr: string;
    notesTopEn: string;
    notesMiddleAr: string;
    notesMiddleEn: string;
    notesBaseAr: string;
    notesBaseEn: string;
  }
>;

export type ShopifyTranslationProduct = {
  handle: string;
  title: string;
  description: string;
};

function toMap(rows: ProductTranslationRow[]): ProductTranslationMap {
  const map: ProductTranslationMap = {};
  for (const r of rows) {
    map[r.handle] = {
      titleAr: r.title_ar ?? "",
      titleEn: r.title_en ?? "",
      descriptionAr: r.description_ar ?? "",
      descriptionEn: r.description_en ?? "",
      notesTopAr: r.notes_top_ar ?? "",
      notesTopEn: r.notes_top_en ?? "",
      notesMiddleAr: r.notes_middle_ar ?? "",
      notesMiddleEn: r.notes_middle_en ?? "",
      notesBaseAr: r.notes_base_ar ?? "",
      notesBaseEn: r.notes_base_en ?? "",
    };
  }
  return map;
}

/** عامّة — تُستخدم في الواجهة لكلّ الزوّار. */
export const getProductTranslations = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ map: ProductTranslationMap }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("product_translations")
      .select("handle,title_ar,title_en,description_ar,description_en,source,updated_at,notes_top_ar,notes_top_en,notes_middle_ar,notes_middle_en,notes_base_ar,notes_base_en");
    return { map: toMap((data ?? []) as ProductTranslationRow[]) };
  },
);

/** أدمن — قائمة كاملة للتحرير. */
export const adminListProductTranslations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ rows: ProductTranslationRow[] }> => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("product_translations")
      .select("handle,title_ar,title_en,description_ar,description_en,source,updated_at,notes_top_ar,notes_top_en,notes_middle_ar,notes_middle_en,notes_base_ar,notes_base_en")
      .order("handle");
    return { rows: (data ?? []) as ProductTranslationRow[] };
  });

/** أدمن — حفظ يدويّ لصفّ واحد. */
export const adminUpsertProductTranslation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    lazyValidator(() => import("./productTranslations.schemas").then((m) => m.upsertTranslationSchema)),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("product_translations").upsert(
      {
        handle: data.handle,
        title_ar: data.title_ar || null,
        title_en: data.title_en || null,
        description_ar: data.description_ar || null,
        description_en: data.description_en || null,
        source: "manual",
      },
      { onConflict: "handle" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** أدمن — حذف صفّ (العودة لنصّ Shopify). */
export const adminDeleteProductTranslation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    lazyValidator(() => import("./productTranslations.schemas").then((m) => m.handleOnlySchema)),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("product_translations")
      .delete()
      .eq("handle", data.handle);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/**
 * أدمن — استخراج handles المنتجات من Shopify Storefront API دون ترجمة.
 * يُنشئ صفوف ترجمة فارغة لكلّ منتج غير موجود محلياً.
 */
export const adminImportProductHandles = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    lazyValidator(() => import("./productTranslations.schemas").then((m) => m.emptySchema), {}),
  )
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { importProductHandles } = await import("./productTranslations.server");
    return importProductHandles(supabaseAdmin);
  });

export const adminListShopifyTranslationProducts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ items: ShopifyTranslationProduct[] }> => {
    await assertAdmin(context.userId);
    const { fetchAllProducts } = await import("./productTranslations.server");
    return { items: await fetchAllProducts() };
  });

/**
 * أدمن — ترجمة آليّة لكلّ المنتجات الناقصة عبر Lovable AI.
 * `force: true` يعيد ترجمة كلّ المنتجات (يتجاوز الصفوف اليدويّة أيضاً).
 */
export const adminAutoTranslateProducts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    lazyValidator(() => import("./productTranslations.schemas").then((m) => m.autoTranslateSchema), {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { translateProductsBatch } = await import("./productTranslations.server");
    return translateProductsBatch(supabaseAdmin, data.force, data.limit);
  });

export const adminAutoTranslateSingleProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    lazyValidator(() => import("./productTranslations.schemas").then((m) => m.autoTranslateSingleSchema)),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { translateSingleProduct } = await import("./productTranslations.server");
    return translateSingleProduct(
      supabaseAdmin,
      { handle: data.handle, title: data.title, description: data.description },
      data.force,
    );
  });

/** أدمن — إعادة ترجمة حقل واحد (اسم/وصف) باتجاه محدَّد، بدون حفظ. */
export const adminTranslateField = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    lazyValidator(() => import("./productTranslations.schemas").then((m) => m.translateFieldSchema)),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { translateField } = await import("./productTranslations.server");
    return { text: await translateField(data) };
  });
